import React from 'react';
import {
  Box,
  CircularProgress,
  Typography,
  LinearProgress
} from '@mui/material';

function LoadingSpinner({ 
  message = 'Loading...', 
  size = 40, 
  variant = 'circular',
  showProgress = false,
  progress = 0 
}) {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '200px',
        p: 3,
        textAlign: 'center'
      }}
    >
      {/* Circular Loading Spinner */}
      {variant === 'circular' && (
        <CircularProgress
          size={size}
          thickness={4}
          sx={{
            color: 'primary.main',
            mb: 2
          }}
        />
      )}

      {/* Linear Progress Bar */}
      {variant === 'linear' && (
        <Box sx={{ width: '100%', maxWidth: 300, mb: 2 }}>
          <LinearProgress
            variant={showProgress ? 'determinate' : 'indeterminate'}
            value={showProgress ? progress : undefined}
            sx={{
              height: 8,
              borderRadius: 4,
              backgroundColor: 'rgba(0, 0, 0, 0.08)',
              '& .MuiLinearProgress-bar': {
                borderRadius: 4,
              }
            }}
          />
          {showProgress && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mt: 1 }}
            >
              {Math.round(progress)}%
            </Typography>
          )}
        </Box>
      )}

      {/* Loading Message */}
      <Typography
        variant="body1"
        color="text.secondary"
        sx={{
          fontWeight: 500,
          maxWidth: 300
        }}
      >
        {message}
      </Typography>

      {/* Truck Icon Animation for Branding */}
      {message.toLowerCase().includes('truck') && (
        <Box
          sx={{
            mt: 2,
            fontSize: '2rem',
            animation: 'pulse 2s infinite',
            '@keyframes pulse': {
              '0%': { opacity: 0.6 },
              '50%': { opacity: 1 },
              '100%': { opacity: 0.6 }
            }
          }}
        >
          🚚
        </Box>
      )}
    </Box>
  );
}

// Full Screen Loading Overlay Component
export function FullScreenLoader({ message = 'Loading...', showProgress = false, progress = 0 }) {
  return (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(255, 255, 255, 0.9)',
        backdropFilter: 'blur(2px)',
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}
    >
      <Box
        sx={{
          backgroundColor: 'background.paper',
          borderRadius: 2,
          p: 4,
          boxShadow: 3,
          minWidth: 280,
          textAlign: 'center'
        }}
      >
        <LoadingSpinner
          message={message}
          size={48}
          variant={showProgress ? 'linear' : 'circular'}
          showProgress={showProgress}
          progress={progress}
        />
      </Box>
    </Box>
  );
}

// Inline Loading Component (for smaller areas)
export function InlineLoader({ message = 'Loading...', size = 24 }) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 2,
        p: 2
      }}
    >
      <CircularProgress size={size} thickness={4} />
      <Typography variant="body2" color="text.secondary">
        {message}
      </Typography>
    </Box>
  );
}

// Loading Button Component (for form submissions)
export function LoadingButton({ 
  loading = false, 
  children, 
  loadingText = 'Submitting...', 
  ...buttonProps 
}) {
  return (
    <Box sx={{ position: 'relative' }}>
      <button
        {...buttonProps}
        disabled={loading || buttonProps.disabled}
        style={{
          ...buttonProps.style,
          opacity: loading ? 0.7 : 1
        }}
      >
        {loading ? loadingText : children}
      </button>
      {loading && (
        <CircularProgress
          size={20}
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            marginTop: '-10px',
            marginLeft: '-10px'
          }}
        />
      )}
    </Box>
  );
}

export default LoadingSpinner;